export default (allSkills: string[], initialCount: number) => ({
  query: "",
  suggestions: [] as string[],
  selectedSkills: [] as string[],
  isLoading: false,
  isLoadingMore: false,
  page: 1,
  hasMore: true,
  totalResults: initialCount || 0,
  filters: {
    seniority: "",
    jobType: "",
    jobMode: "",
    country: "",
    timezone: "",
    glassdoor: "",
    clutch: "",
  } as Record<string, string>,
  allSkills: allSkills || [],

  // Tracks which filter combinations have already been reported for analytics
  _lastTrackedFilters: "",

  get hasFilters() {
    return (
      this.selectedSkills.length > 0 ||
      Object.values(this.filters).some((v) => v !== "")
    );
  },

  init() {
    // Initial check if we have enough jobs to scroll
    this.hasMore = this.totalResults >= 12;

    // Load from local storage
    const saved = localStorage.getItem("remoteshell_state");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.selectedSkills = parsed.selectedSkills || [];
        this.filters = { ...this.filters, ...parsed.filters };
      } catch (e) {
        console.error("Failed to load filters", e);
      }
    }

    // Notify children components about the loaded filters
    this.$nextTick(() => {
      this.$dispatch("initialize-filters", this.filters);
    });

    // Trigger fetch if we have restored filters
    if (this.hasFilters) {
      this.fetchJobs();
    }

    this.$watch("query", (value: string) => {
      if (value.length < 2) {
        this.suggestions = [];
        return;
      }

      const term = value.toLowerCase();

      this.suggestions = this.allSkills.filter(
        (skill) =>
          skill.toLowerCase().includes(term) &&
          !this.selectedSkills.includes(skill),
      );
    });
  },

  save() {
    localStorage.setItem(
      "remoteshell_state",
      JSON.stringify({
        selectedSkills: this.selectedSkills,
        filters: this.filters,
      }),
    );
  },

  getQueryParams() {
    const params = new URLSearchParams();
    if (this.filters.seniority)
      params.append("seniority", this.filters.seniority);
    if (this.filters.jobType) params.append("jobType", this.filters.jobType);
    if (this.filters.jobMode) params.append("jobMode", this.filters.jobMode);
    if (this.filters.country) params.append("country", this.filters.country);
    if (this.filters.timezone) params.append("timezone", this.filters.timezone);
    if (this.filters.glassdoor)
      params.append("glassdoor", this.filters.glassdoor);
    if (this.filters.clutch) params.append("clutch", this.filters.clutch);

    this.selectedSkills.forEach((skill) => {
      params.append("skills", skill);
    });
    return params;
  },

   trackFiltersIfChanged() {
     const current = JSON.stringify({
       filters: this.filters,
       skills: this.selectedSkills.slice().sort(),
     });

     if (current === this._lastTrackedFilters) {
       return;
     }

     this._lastTrackedFilters = current;

     // Fire a lightweight beacon to let the backend know this filter
     // combination is in use, without tying it to every paginated
     // /partials/jobs request.
     try {
       const payload = {
         metrics: [
           {
             name: "FILTER_USAGE",
             value: 1,
             path: window.location.pathname,
           },
         ],
       };

       const blob = new Blob([JSON.stringify(payload)], {
         type: "application/json",
       });

       if (navigator.sendBeacon) {
         navigator.sendBeacon("/api/v1/client-metrics", blob);
       } else {
         fetch("/api/v1/client-metrics", {
           method: "POST",
           body: blob,
           keepalive: true,
           headers: {
             "Content-Type": "application/json",
           },
         }).catch(() => {});
       }
     } catch (e) {
       // Best-effort only; ignore failures
     }
   },

  async fetchJobs() {
    this.isLoading = true;
    this.page = 1;
    this.hasMore = true;
    const params = this.getQueryParams();
    params.append("page", this.page.toString());

    try {
      const response = await fetch("/partials/jobs?" + params.toString());
      if (response.ok) {
        const html = await response.text();
        const container = document.getElementById("job-list-container");
        if (container) {
          container.innerHTML = html;

          // Update results from the data-total attribute in the returned HTML
          const listDiv = container.querySelector(
            "[data-total]",
          ) as HTMLElement;
          if (listDiv) {
            const count = parseInt(listDiv.dataset.total || "0") || 0;
            this.totalResults = count;
            this.hasMore = count >= 12; // Check if we might have more pages
          } else {
            this.hasMore = false;
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch jobs", error);
    } finally {
      this.isLoading = false;
      this.trackFiltersIfChanged();
    }
  },

  async loadMore() {
    if (this.isLoading || this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    this.page++;
    const params = this.getQueryParams();
    params.append("page", this.page.toString());

    try {
      const response = await fetch("/partials/jobs?" + params.toString());
      if (response.ok) {
        const html = await response.text();

        // Create a temp container to check content
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const listDiv = tempDiv.querySelector("[data-total]");

        // Extract cards
        const cards = tempDiv.querySelectorAll(".card"); // Assuming JobCard has .card class

        if (cards.length > 0) {
          // Append cards to the existing list (assuming the list is a grid)
          const container = document.getElementById("job-list-container");
          if (container) {
            const grid = container.querySelector(".grid");

            if (grid) {
              cards.forEach((card) => grid.appendChild(card));
            } else {
              // Fallback if structure is unexpected
              container.insertAdjacentHTML("beforeend", html);
            }
          }

          // Check if we received a full page (assuming limit is 12)
          if (cards.length < 12) {
            this.hasMore = false;
          }
        } else {
          this.hasMore = false;
        }
      }
    } catch (error) {
      console.error("Failed to load more jobs", error);
      this.hasMore = false;
    } finally {
      this.isLoadingMore = false;
    }
  },

  addSkill(skill: string) {
    if (!skill) return;

    // Find if there's a matching skill in allSkills (case-insensitive)
    const normalizedInput = skill.trim().toLowerCase();
    const existingSkill = this.allSkills.find(
      (s) => s.toLowerCase() === normalizedInput,
    );

    // Use the existing skill (correct casing) if found, otherwise use the input as-is
    const skillToAdd = existingSkill || skill.trim();

    if (!this.selectedSkills.includes(skillToAdd)) {
      this.selectedSkills.push(skillToAdd);
      this.query = "";
      this.suggestions = [];
      this.save();
      this.fetchJobs();
    }
  },

  removeSkill(skill: string) {
    this.selectedSkills = this.selectedSkills.filter((s) => s !== skill);
    this.save();
    this.fetchJobs();
  },

  clearAll() {
    this.selectedSkills = [];
    this.query = "";
    this.filters = {
      seniority: "",
      jobType: "",
      jobMode: "",
      country: "",
      timezone: "",
      glassdoor: "",
      clutch: "",
    };

    this.$dispatch("initialize-filters", this.filters); // Reset dropdowns UI
    this.save();
    this.fetchJobs();
  },

  updateFilter(detail: { id: string; value: string }) {
    if (detail && detail.id && this.filters.hasOwnProperty(detail.id)) {
      this.filters[detail.id] = detail.value;
      this.save();
      this.fetchJobs();
    }
  },
});
